import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { adminStrings } from '../i18n/admin';
import { engagementStrings } from '../i18n/engagement';
import { entryStrings } from '../i18n/entry';
import { review01Strings } from '../i18n/review01';
import { review02Strings } from '../i18n/review02';
import { review03Strings } from '../i18n/review03';
import { review04Strings } from '../i18n/review04';
import { revisionStrings } from '../i18n/revision';
import { strings } from '../i18n/strings';
import { applyTerms } from '../i18n/terms';
import type { L10n, Locale } from '../types';
const STORAGE_KEY = 'beelyv.locale';

/** Where the active language came from. A manual choice always wins. */
export type LocaleSource = 'system' | 'manual';
interface LocaleValue {
  locale: Locale;
  source: LocaleSource;
  /** Manual choice. Persists and overrides system detection from now on. */
  setLocale: (locale: Locale) => void;
  /** Drop the manual choice and follow the device language again. */
  followSystemLocale: () => void;
  systemLocale: Locale;
  /** Translate a dictionary key, with optional {token} interpolation. */
  t: (key: string, vars?: Record<string, string | number>) => string;
  /** Pick the active language out of localized mock content. */
  tl: (value: L10n) => string;
  /** Day unit agreeing with a count: 1 → "dia"/"day", otherwise "dias"/"days". */
  dayUnit: (n: number) => string;
}

/**
 * Singular/plural safety net for any interpolated day count, applied to every
 * translated string: "1 dias" → "1 dia", "1 days" → "1 day". Explicit agreement
 * is still done with `dayUnit` wherever the unit is rendered separately.
 */
function fixDayPlurals(text: string): string {
  return text.replace(/(^|[^\d.,])1 dias\b/g, '$11 dia').replace(/(^|[^\d.,])1 days\b/g, '$11 day');
}
const LocaleContext = createContext<LocaleValue | null>(null);

/**
 * Device language → product language.
 * Portuguese → pt-BR · English → en-US · anything else → pt-BR.
 */
export function detectSystemLocale(): Locale {
  if (typeof navigator === 'undefined') return 'pt-BR';
  const primary = (navigator.language || '').toLowerCase();
  if (primary.startsWith('en')) return 'en-US';
  return 'pt-BR';
}
function readStored(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return value === 'pt-BR' || value === 'en-US' ? value : null;
  } catch {
    return null;
  }
}
function writeStored(value: Locale | null) {
  try {
    if (value) window.localStorage.setItem(STORAGE_KEY, value);else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable: preference lives for the session only */
  }
}
export function LocaleProvider({
  initialLocale = 'system',
  children
}: {
  /** 'system' follows the stored manual choice, else the device language. */
  initialLocale?: Locale | 'system';
  children: React.ReactNode;
}) {
  const systemLocale = useMemo(() => detectSystemLocale(), []);
  const [state, setState] = useState<{
    locale: Locale;
    source: LocaleSource;
  }>(() => {
    if (initialLocale !== 'system') return {
      locale: initialLocale,
      source: 'manual'
    };
    const stored = readStored();
    return stored ? {
      locale: stored,
      source: 'manual'
    } : {
      locale: systemLocale,
      source: 'system'
    };
  });
  const setLocale = useCallback((locale: Locale) => {
    writeStored(locale);
    setState({
      locale,
      source: 'manual'
    });
  }, []);
  const followSystemLocale = useCallback(() => {
    writeStored(null);
    setState({
      locale: systemLocale,
      source: 'system'
    });
  }, [systemLocale]);
  const {
    locale,
    source
  } = state;
  const t = useCallback((key: string, vars?: Record<string, string | number>) => {
    const template = review04Strings[locale][key] ?? review03Strings[locale][key] ?? review02Strings[locale][key] ?? review01Strings[locale][key] ?? revisionStrings[locale][key] ?? engagementStrings[locale][key] ?? strings[locale][key] ?? adminStrings[locale][key] ?? entryStrings[locale][key] ?? review04Strings['pt-BR'][key] ?? review03Strings['pt-BR'][key] ?? review02Strings['pt-BR'][key] ?? review01Strings['pt-BR'][key] ?? revisionStrings['pt-BR'][key] ?? engagementStrings['pt-BR'][key] ?? strings['pt-BR'][key] ?? adminStrings['pt-BR'][key] ?? entryStrings['pt-BR'][key] ?? key;
    const filled = vars ? Object.keys(vars).reduce((acc, token) => acc.split(`{${token}}`).join(String(vars[token])), template) : template;
    return fixDayPlurals(applyTerms(filled));
  }, [locale]);
  const tl = useCallback((value: L10n) => fixDayPlurals(applyTerms(value[locale])), [locale]);
  const dayUnit = useCallback((n: number) => t(n === 1 ? 'common.day' : 'common.days'), [t]);
  const value = useMemo(() => ({
    locale,
    source,
    setLocale,
    followSystemLocale,
    systemLocale,
    t,
    tl,
    dayUnit
  }), [locale, source, setLocale, followSystemLocale, systemLocale, t, tl, dayUnit]);
  return <LocaleContext.Provider value={value} data-id="element-1563" data-mp="wnat78zssba4">{children}</LocaleContext.Provider>;
}
export function useLocale(): LocaleValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside LocaleProvider');
  return ctx;
}